import { useState } from 'react';
import {
  ArrowDownToLine,
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
  EyeOff,
  Lock,
  Plus,
  Trash2,
  Unlock,
} from 'lucide-react';
import { useEditor } from '../../state/store';
import { Hint, Panel } from '../widgets/common';

/** Layer stack: topmost layer first, as in every other design tool. */
export function LayersPanel(): JSX.Element {
  const revision = useEditor((s) => s.revision);
  const store = useEditor.getState();
  const tab = store.active();
  const [renaming, setRenaming] = useState<string | null>(null);

  const ordered = [...tab.doc.layers].reverse();

  return (
    <Panel
      title={`Layers (${tab.doc.layers.length})`}
      collapsible={false}
      actions={
        <>
          <button className="icon-btn" title="Add layer" onClick={() => store.addLayer()}>
            <Plus size={14} />
          </button>
          <button
            className="icon-btn"
            title="Duplicate layer"
            onClick={() => store.duplicateLayer(tab.doc.activeLayerId)}
          >
            <Copy size={13} />
          </button>
          <button
            className="icon-btn"
            title="Merge into the layer below"
            onClick={() => store.mergeLayerDown(tab.doc.activeLayerId)}
          >
            <ArrowDownToLine size={13} />
          </button>
          <button
            className="icon-btn"
            title="Delete layer"
            onClick={() => store.removeLayer(tab.doc.activeLayerId)}
          >
            <Trash2 size={13} />
          </button>
        </>
      }
    >
      <div className="list" key={revision}>
        {ordered.map((layer) => {
          const index = tab.doc.layerIndex(layer.id);
          const active = layer.id === tab.doc.activeLayerId;
          return (
            <div
              key={layer.id}
              className={`item${active ? ' active' : ''}`}
              onClick={() => store.setActiveLayer(layer.id)}
              onDoubleClick={() => setRenaming(layer.id)}
            >
              <button
                className={`icon-btn${layer.visible ? ' on' : ''}`}
                title={layer.visible ? 'Hide layer' : 'Show layer'}
                onClick={(e) => {
                  e.stopPropagation();
                  store.toggleLayerVisible(layer.id);
                }}
              >
                {layer.visible ? <Eye size={13} /> : <EyeOff size={13} />}
              </button>
              <button
                className={`icon-btn${layer.locked ? ' on' : ''}`}
                title={layer.locked ? 'Unlock layer' : 'Lock layer'}
                onClick={(e) => {
                  e.stopPropagation();
                  store.toggleLayerLocked(layer.id);
                }}
              >
                {layer.locked ? <Lock size={13} /> : <Unlock size={13} />}
              </button>

              {renaming === layer.id ? (
                <input
                  className="input"
                  autoFocus
                  defaultValue={layer.name}
                  onBlur={(e) => {
                    store.renameLayer(layer.id, e.target.value || layer.name);
                    setRenaming(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                    if (e.key === 'Escape') setRenaming(null);
                    e.stopPropagation();
                  }}
                />
              ) : (
                <span className="name" title="Double-click to rename">
                  {layer.name}
                </span>
              )}

              <button
                className="icon-btn"
                title="Move up"
                disabled={index === tab.doc.layers.length - 1}
                onClick={(e) => {
                  e.stopPropagation();
                  store.moveLayer(layer.id, index + 1);
                }}
              >
                <ChevronUp size={13} />
              </button>
              <button
                className="icon-btn"
                title="Move down"
                disabled={index === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  store.moveLayer(layer.id, index - 1);
                }}
              >
                <ChevronDown size={13} />
              </button>
            </div>
          );
        })}
      </div>
      <Hint>Drawing always goes to the highlighted layer. Double-click a name to rename it.</Hint>
    </Panel>
  );
}
